import { useMemo } from "react";

import {
  InfiniteData,
  QueryKey,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { entitiesApi } from "src/api/endpoints";
import { SubSchema } from "src/api/flowTypes";
import { useCurrentWorkspace } from "src/api/queries";
import {
  Address,
  EntityCaseSummary as EntityCaseSummaryGenerated,
  EntityCaseSummaryStatusEnum,
  EntityResourcePage as EntityResourcePageGenerated,
  EntityResourcePageRelatedV2,
  EntityResourcePageV2 as EntityResourcePageV2Generated,
  GetEnrichedEntityBySchemaEntitiesApiV1EntitiesEnvSchemaEntityIdRelatedGetEnvEnum,
  GetEntityBySchemaEntitiesApiV1EntityEnvSchemaEntityIdGetEnvEnum,
  GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetEnvEnum,
  GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetPageDirectionEnum,
  GetSchemaJsonSchemaEntitiesApiV1SchemasSchemaJsonSchemaGetSchemaEnum,
  ListEntitiesBySchemaV2EntitiesApiV2EntitiesEnvSchemaGetEnvEnum,
  ListSchemasEntitiesApiV1SchemasSchemaGetSchemaEnum,
  PutSchemaEntitiesApiV1SchemaSchemaEntityIdPutSchemaEnum,
  Relation,
  RevealEntityFieldEntitiesApiV1EntityEnvSchemaEntityIdRevealFieldGetEnvEnum,
} from "src/clients/entities/api";
import {
  EnumColor,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import {
  entityIcons,
  getEntityKeys,
  transformToFrontendEntity,
} from "src/entities/entityView/utils";
import { Environment } from "src/hooks/useEnvironment";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { DashboardPageParams } from "src/router/urls";
import { DO_NOT_RETRY_ON_404 } from "src/utils/query";
import { encodeUrlParams } from "src/utils/url";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const entitiesKeys = {
  all: (workspaceId: string, schema: string) =>
    [workspaceId, "entities", schema] as const,
  schemaEntities: (
    workspaceId: string,
    schema: string,
    env?: Environment,
    filters?: string[],
    limit?: number,
  ) =>
    [
      ...entitiesKeys.all(workspaceId, schema),
      env,
      { filters, limit },
    ] as const,
  entity: (wsId: string, schema: string, env: Environment, entityId: string) =>
    [...entitiesKeys.all(wsId, schema), env, entityId] as const,
};

const entitySchemaKeys = {
  all: (baseUrlorWorkspaceId: string) => [
    "entitySchemas",
    baseUrlorWorkspaceId,
  ],
  allJsonSchemas: (baseUrlorWorkspaceId: string) => [
    "entitySchemasJsonSchema",
    baseUrlorWorkspaceId,
  ],
  entitySchema: (baseUrlorWorkspaceId: string, schema: string) =>
    [...entitySchemaKeys.all(baseUrlorWorkspaceId), schema] as const,
};

export const ENTITIES_PAGE_SIZE = 50;
export const RELATED_ENTITIES_PAGE_SIZE = 10;

type UseEntitiesParamsV1 = {
  schema: string;
  env: Environment;
  filters?: string[];
  limit?: number;
};

type UseEntitiesParams = {
  schema: string;
  env: Environment;
  filters?: Record<string, string | number | undefined>;
  limit?: number;
};

type UseEntityNavigationParams = UseEntitiesParams & {
  pageCursor?: string;
  pageDirection?: "forward" | "backward";
  options?: { enabled: boolean };
};

export type EntityFieldType = PropertyDefinitionOutputTypeEnum;

export enum Cardinality {
  ONE = "1",
  ONE_TO_MANY = "1-to-many",
}

export type PredefinedValue = { _internal_name: string; _color: EnumColor };

export type EntitySchemaProperty = {
  _type: EntityFieldType;
  _display_name: string;
  _indexed: boolean;
  _rel_schema?: string;
  _backref?: string;
  _cardinality?: Cardinality;
  _values?: PredefinedValue[];
  _order?: number;
  _sensitive?: boolean;
  _masking?: Masking;
  // Form-only markers for newly-added, not-yet-saved properties. `_name` holds
  // the user-typed field name that becomes the property key on save. Both are
  // stripped before the schema is persisted (see remapNewProperties).
  _isNew?: boolean;
  _name?: string;
};

type Masking = {
  _strategy: "full" | "partial" | "hash";
  _options:
    | {
        _mask: string;
      }
    | {
        _keep_left: number;
        _keep_right: number;
        _mask_character: string;
      }
    | {
        _algorithm: "sha256" | "sha512";
      };
};

export type PredefinedValuesSchemaProperty = EntitySchemaProperty & {
  _type:
    | PropertyDefinitionOutputTypeEnum.ENUM
    | PropertyDefinitionOutputTypeEnum.TAGS;
  _values: PredefinedValue[];
};

export type EntityPropertyValue = string | string[] | null | object | Address;

export type TableRow = EntitySchemaProperty & {
  value: EntityPropertyValue;
  isId: boolean;
  _id: string;
  _schema: string;
  _display_name: string;
  _cardinality?: Cardinality;
  _property_key: string;
};

export type EntitySchemaResourceDataplane = {
  _id: string;
  _schema: "Entity";
  _etag: number;
  _related: Record<string, unknown>;
  _updatedAt: string;
  _display_name_singular: string;
  _display_name_plural: string;
  _display_symbol: keyof typeof entityIcons;
  _primary_property: string;
  _secondary_property: string;
  _order: number;
};

export type EntitySchemaResource = EntitySchemaResourceDataplane & {
  properties: {
    [key: string]: EntitySchemaProperty;
  };
};

export type EntityResourceReservedKeys = {
  _id: string;
  _schema: string;
  _etag: number;
  _related: Record<string, unknown>;
  _updated_at: string;
  _fk_pp?: Record<`${string}.${string}`, string>; // foreginKeyToPrimaryProperty
};

type EntityCaseSummary = Omit<EntityCaseSummaryGenerated, "assignees"> & {
  assignees: string[];
};

type EntityResourceWithReviewStatusReservedKeys = EntityResourceReservedKeys & {
  entity_case_summary: EntityCaseSummary;
};

export type EntityResource = EntityResourceReservedKeys & {
  properties: {
    [key: string]: EntityPropertyValue;
  };
};

export type FlatEntityResource = EntityResourceReservedKeys & {
  id: string;
  [key: string]: EntityPropertyValue;
};

export type EntityResourceWithReviewStatus =
  EntityResourceWithReviewStatusReservedKeys & {
    properties: {
      id: string;
      [key: string]: EntityPropertyValue;
    };
  };

export type EntityResourceProperties = Pick<EntityResource, "properties">;

// Extend the generated type to get correct types not just "objects"
type EntityResourcePage = Omit<EntityResourcePageGenerated, "entities"> & {
  entities: EntityResource[];
};

type EntityResourcePageV2 = Omit<EntityResourcePageV2Generated, "entities"> & {
  entities: EntityResourceWithReviewStatus[];
};

// When we fetch schemas we have no cases
export type EntitySchemaResourcePage = Omit<
  EntityResourcePageGenerated,
  "entities" | "cases"
> & {
  entities: EntitySchemaResource[];
  next_page_token: string;
  previous_page_token?: string;
};

type UseEntityParams = UseEntitiesParamsV1 & {
  entityId: string | null | undefined;
  enabled?: boolean;
  staleTime?: number;
};

export type IpAddressPropertyValue = {
  ipv4: string;
  ipv6: string;
};

export type CoordinatesPropertyValue = {
  lat: number;
  long: number;
};

export type FilePropertyValue = {
  document_url: string;
  name?: string;
  uploaded_at?: string;
};

export type TransformedRelatedEntities = {
  schema: EntitySchemaResource;
  entities: EntityResource[];
};

export type RelatedEntitiesDataPlane = {
  entities: Record<
    string,
    EntityResourceReservedKeys | EntitySchemaResourceDataplane
  >;
};

export type GroupedEntity = {
  schema: EntitySchemaResourceDataplane;
  entities: EntityResourceReservedKeys[];
};

export type RelationDataPlane = {
  property: Relation;
  entity: EntityResource;
};

export type RelatedEntitiesPage = {
  relations: GroupedRelation[];
  next_page_token?: string;
};

export type GroupedRelation = {
  schemaId: string;
  entitySchemaId: string;
  property: string;
  entities: EntityResource[];
  schema?: EntitySchemaResource;
  relatedSchema?: EntitySchemaResource;
  entitySchema?: EntitySchemaResource;
};

export type EntityManualReviewCase = {
  assignees: string[];
  status: EntityCaseSummaryStatusEnum;
};

export type EntityManualReviewCases = { [key: string]: EntityManualReviewCase };

export type SchemaRelationship = {
  from_schema: string;
  property_name: string;
  target_schema: string;
};

const transformToFrontendTypes = <S extends "schemas" | "entity">(
  data: EntityResourcePageGenerated,
) => {
  return {
    ...data,
    entities: data.entities.map((entity) =>
      getEntityKeys(
        entity as EntitySchemaResourceDataplane | EntityResourceReservedKeys,
      ),
    ),
  } as S extends "schemas" ? EntitySchemaResourcePage : EntityResourcePage;
};

const transformToFrontendEntities = (
  data: EntityResourcePageV2Generated,
): EntityResourcePageV2 =>
  ({
    ...data,
    entities: data.entities.map((entity) =>
      getEntityKeys(
        entity as unknown as EntityResourceWithReviewStatusReservedKeys,
      ),
    ),
  }) as EntityResourcePageV2;

const useEntitiesClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () => (workspace?.base_url ? entitiesApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );
};

export const useEntitySchemas = ({
  baseUrl,
  options,
}: {
  baseUrl: string;
  options?: { enabled: boolean };
}) => {
  const apiClient = useMemo(() => entitiesApi(baseUrl), [baseUrl]);

  return useQuery<
    EntityResourcePageGenerated,
    AxiosError,
    EntitySchemaResourcePage
  >({
    queryKey: entitySchemaKeys.all(baseUrl),
    queryFn: async () => {
      const response = await apiClient.listSchemasEntitiesApiV1SchemasSchemaGet(
        {
          schema: ListSchemasEntitiesApiV1SchemasSchemaGetSchemaEnum.ENTITY,
          limit: 100,
        },
      );

      return response.data;
    },
    select: (data) => {
      return transformToFrontendTypes<"schemas">(data);
    },
    enabled: baseUrl !== "" && options?.enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useEntities = (
  { schema, env, filters, limit }: UseEntitiesParams,
  { enabled }: { enabled?: boolean } = { enabled: true },
) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useEntitiesClient();

  const PAGE_COLUMN = "_updated_at";

  return useInfiniteQuery<
    EntityResourcePageV2Generated,
    AxiosError,
    InfiniteData<EntityResourcePageV2>,
    QueryKey,
    {
      cursor: string | undefined;
      cursorDirection: "forward" | "backward";
    }
  >({
    queryKey: entitiesKeys.schemaEntities(
      wsId,
      schema,
      env,
      Object.entries(filters ?? {}).map(([key, value]) => `${key}=${value}`),
      limit,
    ),
    queryFn: async ({ pageParam }) => {
      const response =
        await apiClient!.listEntitiesBySchemaV2EntitiesApiV2EntitiesEnvSchemaGet(
          {
            env: env as unknown as ListEntitiesBySchemaV2EntitiesApiV2EntitiesEnvSchemaGetEnvEnum,
            schema,
          },
          {
            params: encodeUrlParams({
              ...filters,
              "page[limit]": limit,
              "page[cursor]": pageParam.cursor,
              "page[direction]": pageParam.cursorDirection,
              "page[column]": PAGE_COLUMN,
            }),
          },
        );

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.next_cursor || lastPage.entities.length !== limit) {
        return undefined;
      }

      return {
        cursor: lastPage.next_cursor,
        cursorDirection: "backward",
      };
    },
    initialPageParam: {
      cursor: undefined,
      cursorDirection: "backward",
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.previous_cursor || firstPage.entities.length !== limit) {
        return undefined;
      }

      return {
        cursor: firstPage.previous_cursor,
        cursorDirection: "forward",
      };
    },
    select: (data) => ({
      pages: data.pages.map(transformToFrontendEntities),
      pageParams: data.pageParams,
    }),
    enabled: !!apiClient && enabled,
    retry: (failureCount, error) => {
      return (
        error.response?.status !== 404 &&
        error.response?.status !== 403 &&
        failureCount < 3
      );
    },
  });
};

export const useEnrichedEntityData = ({
  entitySchema,
  entityId,
  env,
  baseUrl,
}: {
  entitySchema: string;
  entityId: string;
  env: Environment;
  baseUrl: string;
}) => {
  const apiClient = useMemo(() => entitiesApi(baseUrl), [baseUrl]);

  return useQuery({
    queryKey: ["entityEnrichmentData", env, entitySchema, entityId],
    queryFn: async () => {
      const response =
        await apiClient.getEnrichedEntityBySchemaEntitiesApiV1EntitiesEnvSchemaEntityIdRelatedGet(
          {
            env: env as unknown as GetEnrichedEntityBySchemaEntitiesApiV1EntitiesEnvSchemaEntityIdRelatedGetEnvEnum,
            schema: entitySchema,
            entityId: entityId,
          },
        );
      return response.data;
    },
    enabled: !!entityId,
  });
};

/**
 * This query is used for the Entity Details header to handle the navigation
 * It calls the Entity list endpoint, filtering by id and limit 1 to fetch the current entity
 * After that, 2 additional calls are made to fetch the previous and next entities
 * using the page tokens from the first call and without any filters
 */
export const useEntityNavigation = ({
  schema,
  env,
  filters,
  pageCursor: pageCursor,
  pageDirection: pageDirection,
  limit: pageLimit = 1,
  options,
}: UseEntityNavigationParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useEntitiesClient();

  return useQuery<
    EntityResourcePageV2Generated,
    AxiosError,
    EntityResourcePageV2,
    QueryKey
  >({
    queryKey: [
      "entityNavigation",
      wsId,
      schema,
      env,
      filters,
      pageLimit,
      pageCursor,
      pageDirection,
    ],
    enabled: options?.enabled,
    queryFn: async () => {
      const response =
        await apiClient!.listEntitiesBySchemaV2EntitiesApiV2EntitiesEnvSchemaGet(
          {
            env: env as unknown as ListEntitiesBySchemaV2EntitiesApiV2EntitiesEnvSchemaGetEnvEnum,
            schema,
          },
          {
            params: encodeUrlParams({
              ...(pageCursor ? {} : filters),
              "page[limit]": pageLimit,
              "page[cursor]": pageCursor,
              "page[direction]": pageDirection,
            }),
          },
        );

      return response.data;
    },
    select: (data) => {
      return transformToFrontendEntities(data);
    },
  });
};

const transformEntity = (data: EntityResourceReservedKeys) => {
  return getEntityKeys(data) as EntityResource;
};

export const useEntity = ({
  schema,
  env,
  entityId,
  enabled,
  staleTime,
}: UseEntityParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useEntitiesClient();

  return useQuery<
    EntityResourceReservedKeys,
    AxiosError,
    EntityResource | undefined
  >({
    queryKey: entitiesKeys.entity(wsId, schema, env, entityId!),
    queryFn: async () => {
      return (
        await apiClient!.getEntityBySchemaEntitiesApiV1EntityEnvSchemaEntityIdGet(
          {
            env: env as unknown as GetEntityBySchemaEntitiesApiV1EntityEnvSchemaEntityIdGetEnvEnum,
            schema,
            entityId: entityId!,
          },
        )
      ).data as EntityResourceReservedKeys;
    },
    enabled: !!apiClient && !!entityId && enabled,
    select: transformEntity,
    retry: DO_NOT_RETRY_ON_404,
    staleTime,
  });
};

export const useEntitySchemasJsonSchema = ({
  baseUrl,
  options,
}: {
  baseUrl: string;
  options?: Pick<UseQueryOptions<Record<string, SubSchema>>, "enabled">;
}) => {
  const apiClient = useMemo(() => entitiesApi(baseUrl), [baseUrl]);
  return useQuery<Record<string, SubSchema>, Error>({
    queryKey: entitySchemaKeys.allJsonSchemas(baseUrl),
    queryFn: async () => {
      const response =
        await apiClient.getSchemaJsonSchemaEntitiesApiV1SchemasSchemaJsonSchemaGet(
          {
            schema:
              GetSchemaJsonSchemaEntitiesApiV1SchemasSchemaJsonSchemaGetSchemaEnum.ENTITY,
          },
        );

      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: options?.enabled,
  });
};

export const useUpdateEntitySchema = () => {
  const { workspace } = useWorkspaceContext();
  const baseUrl = workspace?.base_url ?? "";

  const apiClient = useEntitiesClient();

  return useMutation({
    mutationFn: async (schema: EntitySchemaResource) => {
      const { properties, ...baseFields } = schema;
      const response =
        await apiClient!.putSchemaEntitiesApiV1SchemaSchemaEntityIdPut({
          schema:
            PutSchemaEntitiesApiV1SchemaSchemaEntityIdPutSchemaEnum.ENTITY,
          entityId: schema._id,
          entityResource: {
            ...baseFields,
            ...properties,
          },
        });

      return transformToFrontendEntity<"schemas">(response.data);
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: entitySchemaKeys.all(baseUrl),
      });
      queryClient.invalidateQueries({
        queryKey: entitySchemaKeys.allJsonSchemas(baseUrl),
      });
      queryClient.invalidateQueries({
        queryKey: entitiesKeys.all(workspace.id, variables._id),
      });
    },
  });
};

export const useRelatedEntitiesByRelationship = ({
  schema,
  entityId,
  env,
  baseUrl,
  relationship,
  enabled,
  pageLimit = RELATED_ENTITIES_PAGE_SIZE,
}: {
  schema: string;
  entityId: string;
  env: Environment;
  baseUrl: string;
  relationship: SchemaRelationship;
  enabled?: boolean;
  pageLimit?: number;
}) => {
  const _apiClient = useMemo(() => entitiesApi(baseUrl), [baseUrl]);

  return useInfiniteQuery<
    EntityResourcePageRelatedV2,
    Error,
    InfiniteData<EntityResourcePageRelatedV2>,
    QueryKey,
    {
      cursor: string | undefined;
      cursorDirection: GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetPageDirectionEnum;
    }
  >({
    queryKey: ["relatedEntities", schema, entityId, relationship, env],
    queryFn: async ({ pageParam }) => {
      const response =
        await _apiClient.getRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGet(
          {
            entitySchema: schema,
            entityId,
            env: env as unknown as GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetEnvEnum,
            fromSchema: relationship.from_schema,
            propertyName: relationship.property_name,
            targetSchema: relationship.target_schema,
          },
          {
            params: {
              "page[limit]": pageLimit,
              "page[cursor]": pageParam.cursor,
              "page[direction]": pageParam.cursorDirection,
            },
          },
        );

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.next_cursor || lastPage.entities.length !== pageLimit) {
        return undefined;
      }

      return {
        cursor: lastPage.next_cursor,
        cursorDirection:
          GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetPageDirectionEnum.FORWARD,
      };
    },
    initialPageParam: {
      cursor: undefined,
      cursorDirection:
        GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetPageDirectionEnum.FORWARD,
    },
    getPreviousPageParam: (firstPage) => {
      if (
        !firstPage.previous_cursor ||
        firstPage.entities.length !== pageLimit
      ) {
        return undefined;
      }

      return {
        cursor: firstPage.previous_cursor,
        cursorDirection:
          GetRelatedEntitiesV2EntitiesApiV2EntitiesEnvEntitySchemaEntityIdRelatedGetPageDirectionEnum.BACKWARD,
      };
    },
    enabled: !!_apiClient && enabled,
  });
};

export const useRevealEntityField = ({
  env,
  schemaId,
  entityId,
  field,
  enabled,
}: {
  env: string;
  schemaId: string;
  entityId: string;
  field: string;
  enabled: boolean;
}) => {
  const apiClient = useEntitiesClient();

  return useQuery({
    queryKey: ["revealEntityField", env, schemaId, entityId, field],
    queryFn: () =>
      apiClient!
        .revealEntityFieldEntitiesApiV1EntityEnvSchemaEntityIdRevealFieldGet({
          env: env as RevealEntityFieldEntitiesApiV1EntityEnvSchemaEntityIdRevealFieldGetEnvEnum,
          schema: schemaId,
          entityId,
          field,
        })
        .then((response) => ({ value: response.data })),
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
};

export type RelationshipProperty = {
  propertyName: string;
  entityIds: string[];
  targetSchema: string;
};

type UseRelatedEntitiesForCopyParams = {
  relationshipProperties: RelationshipProperty[];
  baseUrl: string;
  env: Environment;
};

export const useRelatedEntitiesForCopy = ({
  relationshipProperties,
  baseUrl,
  env,
}: UseRelatedEntitiesForCopyParams) => {
  const apiClient = useMemo(() => entitiesApi(baseUrl), [baseUrl]);

  return useQueries({
    queries: relationshipProperties.flatMap(
      ({ propertyName, entityIds, targetSchema }) =>
        entityIds.map((entityId) => ({
          queryKey: ["relatedEntityForCopy", targetSchema, entityId, env],
          queryFn: async () => {
            const response =
              await apiClient.getEntityBySchemaEntitiesApiV1EntityEnvSchemaEntityIdGet(
                {
                  env: env as unknown as GetEntityBySchemaEntitiesApiV1EntityEnvSchemaEntityIdGetEnvEnum,
                  schema: targetSchema,
                  entityId,
                },
              );
            return {
              data: response.data as EntityResourceReservedKeys,
              propertyName,
              entityId,
            };
          },
          enabled: !!apiClient && !!entityId && !!targetSchema && !!env,
          staleTime: 5 * 60 * 1000,
        })),
    ),
  });
};
